import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, input, model, OnInit, output, ViewEncapsulation } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { LookupService } from "@app/cem/project/services/lookup.service";
import { ProjectState } from '@app/cem/project/services/state/project-state';
import { ErrorService } from '@app/common/services/error.service';
import { PlexEnvironment } from '@app/env';
import { PxdAgGridCustomGroupCellRendererComponent } from '@app/shared/aggrid/pxd-ag-grid-group-cell-renderer';
import { PxdAgGridColumnTypes, PxdAgGridDefaultColumnDef, PxdAgGridOptions, PxdAgGridSideBarOptions, PxdAgGridStatusBarOptions } from '@app/shared/aggrid/pxd-ag-grid-options';
import { PxdAgGridSelectEditorComponent } from '@app/shared/aggrid/pxd-ag-grid-select-editor';
import { TabHeaderComponent } from '@app/shared/components/tab-header/tab-header.component';
import { IProjectCostCodeCostTypeItem, IProjectEstimate } from '@app/shared/interfaces';
import { faIcons } from '@app/shared/library/fa-icons';
import { kendoIcons } from '@app/shared/library/kendo-icons';
import { PhaseStepType, ProjectEstimate } from '@app/shared/models';
import { DeletedDraftEstimateItemDto } from '@app/shared/models/dtos/deleted-draft-estimate-item';
import { IUnitOfMeasureDto } from '@app/shared/models/dtos/dtos';
import { DuplicateInfo } from '@app/shared/models/dtos/duplicate-info';
import { InactiveDraftEstimateItemDto } from '@app/shared/models/dtos/inactive-draft-estimate-item';
import { IItemCostCodeCostTypeDto } from '@app/shared/models/dtos/item-cost-code-cost-type';
import { NewDraftEstimateItemDto } from '@app/shared/models/dtos/new-draft-estimate-item';
import { ProjectEstimateDetailItemDto } from '@app/shared/models/dtos/project-estimate-detail-item';
import { SyncStatuses } from '@app/shared/models/dtos/sync-statuses';
import { UpdatedDraftEstimateItemDto } from '@app/shared/models/dtos/updated-draft-estimate-item';
import { round } from '@app/shared/utilities/math-utilities';
import { ButtonDirective, SplitButtonComponent } from '@progress/kendo-angular-buttons';
import { DialogActionsComponent, DialogComponent } from '@progress/kendo-angular-dialog';
import { DropDownListComponent } from '@progress/kendo-angular-dropdowns';
import { SVGIconComponent } from '@progress/kendo-angular-icons';
import { LoaderComponent } from '@progress/kendo-angular-indicators';
import { NumericTextBoxComponent, RadioButtonDirective } from '@progress/kendo-angular-inputs';
import { AgGridAngular } from 'ag-grid-angular';
import { CellValueChangedEvent, ColDef, ColGroupDef, EditableCallbackParams, ExcelExportParams, GridApi, GridOptions, GridReadyEvent, IRowNode, ITooltipParams } from 'ag-grid-community';
import { eCostCodeCostTypes, ProjectEstimateStatus, ProjectEstimateStatusDescription, ProjectResourceItemType, ProjectStatusDescription, ProjectTypeId } from 'app/shared/data/project-enums';
import moment from 'moment';
import { GlobalCcctCombination } from '../../enums/global-cttt-combination';
import { EstimateState } from '../../models/estimate-state';
import { ProgressDialog, ProgressDialogMessage } from '../../models/progress-dialog';
import { ProjectValidateObject } from '../../models/project-validate-object';
import { ProjectItemSelectComponent } from '../../resources/project-item-select/project-item-select.component';
import { LookupPlusService } from '../../services/lookups/lookup-plus.service';
import { ProjectCustomEndpointService } from '../../services/project-custom-endpoint.service';
import { ProjectEstimateService } from '../../services/project-estimate.service';
import { ProjectHeaderRefreshService } from '../../services/project-header-data-refresh';
import { StandardConfigSyncComponent } from '../../standard-config-sync/standard-config-sync.component';
import { CcctSelectComponent } from '../ccct-select/ccct-select.component';
import { ProjectEstimateDetailService } from './project-estimate-detail.service';

export interface IKeyedPhaseStepSort {
  PSKey: string;
  PhaseStepTypeId: number;
  Phase: string;
  Step: string;
  SortOrder: number,
  PhaseOrder: number,
  StepOrder: number
}

export const DIALOG_MODE = {
  AFE: "AFE",
  PUBLISH: "PUBLISH"
} as const

export type DialogMode = typeof DIALOG_MODE[keyof typeof DIALOG_MODE]

export interface IDialogClosed {
  mode: DialogMode
}
@Component({
  selector: 'app-project-estimate-detail',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './project-estimate-detail.component.html',
  styleUrls: ['./project-estimate-detail.component.scss'],
  imports: [
    AgGridAngular,
    ButtonDirective,
    CcctSelectComponent,
    DialogActionsComponent,
    DialogComponent,
    DropDownListComponent,
    FormsModule,
    LoaderComponent,
    NumericTextBoxComponent,
    ProjectItemSelectComponent,
    RadioButtonDirective,
    ReactiveFormsModule,
    SplitButtonComponent,
    StandardConfigSyncComponent,
    SVGIconComponent,
    TabHeaderComponent,
  ],
})
export class ProjectEstimateDetailComponent implements OnInit {
  private lookupPlusService = inject(LookupPlusService);
  private projectCustomEndpointService = inject(ProjectCustomEndpointService);
  private formBuilder = inject(UntypedFormBuilder);
  private projectEstimateService = inject(ProjectEstimateService);
  private errorService = inject(ErrorService);
  private cdr = inject(ChangeDetectorRef);
  private lookupService = inject(LookupService);
  private projectEstimateDetailService = inject(ProjectEstimateDetailService);
  private projectHeaderRefreshService = inject(ProjectHeaderRefreshService);
  private projectState = inject(ProjectState);
  public environment = inject(PlexEnvironment);

  //! For Dev Testing
  // showDebugger = false;
  public icons = { ...kendoIcons, ...faIcons };
  showEstimateCompareButton: boolean = false;
  isDraftEstimate: boolean = false;
  isFirstTimeLoading: boolean = true;
  disableAllButtons: boolean = false;
  showGeneratingEstimateDialog: boolean = true;
  saving: boolean = false;
  hasPendingChange: boolean = false;
  publishingProject: boolean = false;
  public IUnitOfMeasureDtos: Array<IUnitOfMeasureDto> = new Array<IUnitOfMeasureDto>();
  private itemPhaseStepTypes: Array<PhaseStepType> = [];
  private itemPhasesDistinct: string[] = [];
  private defaultUnitOfMeasure: string = 'Each';
  processComplete = false;
  private projectValidateObject: ProjectValidateObject;
  progressDialog: ProgressDialog = { DialogTitle: '', Message: '', Progress: [], ErrorMessage: [] };
  private progressSteps: Map<string, ProgressDialogMessage> = new Map<string, ProgressDialogMessage>();

  private projectTaxCcctItemId: number;
  private projectContingencyCcctItemId: number;

  headerForm: UntypedFormGroup;
  public showConfirmSyncDialog: boolean;
  public nothingToSyncDialog: boolean;

  public showUpdateAFEDialog: boolean;
  public selectedReasonId: number | null = null;
  public reasonsToUpdateAFE = [];
  columnDefs = [];
  newItems: NewDraftEstimateItemDto[];
  deletedItems: DeletedDraftEstimateItemDto[];
  updatedItems: UpdatedDraftEstimateItemDto[];
  inactiveItems: InactiveDraftEstimateItemDto[];

  selectedDraftItems: ProjectEstimateDetailItemDto[];
  manualItemsInDraftEstimate: ProjectEstimateDetailItemDto[];

  itemsToAddAfterSync: NewDraftEstimateItemDto[];
  itemsToDeleteAfterSync: ProjectEstimateDetailItemDto[];
  itemsToUpdateAfterSync: ProjectEstimateDetailItemDto[];
  manualItemsToDeleteAfterSync: ProjectEstimateDetailItemDto[];

  rowClassRules = {
    'pinned-estimate-item': function (params) {
      return params && params.node && params.node.rowPinned;
    }
  };

  showTopMessage: boolean = false;
  topMessage: string = '   ';
  isCcctDialogOpen: boolean = false;
  isItemsDialogOpen: boolean = false;
  syncTopPinnedRowsByDefault: boolean = false;
  dropdownKey: string = '';

  distinctPhaseStep: Array<IKeyedPhaseStepSort> = [];
  private distinctPhaseStepMap: Map<string, IKeyedPhaseStepSort> = null;
  private rowData: ProjectEstimateDetailItemDto[];
  private userDeletedItems: Map<string, ProjectEstimateDetailItemDto> = new Map<string, ProjectEstimateDetailItemDto>();
  private userEditedItems: Map<string, ProjectEstimateDetailItemDto> = new Map<string, ProjectEstimateDetailItemDto>();
  private newItemId: number = 0;

  private phaseOrderMap: Map<string, number> = new Map();
  private applyTaxAndContingencyFilter: boolean = true;
  public hasEstimateDuplicateItems: boolean = false;
  public duplicateEstimateItems: string[] = [];
  private gridApi: GridApi;
  gridOptions: PxdAgGridOptions = {
    rowSelection: { mode: 'multiRow', headerCheckbox: true, selectAll: 'filtered', enableClickSelection: false },
    cellSelection: true,
    rowGroupPanelShow: 'always',
    defaultColDef: PxdAgGridDefaultColumnDef,
    statusBar: PxdAgGridStatusBarOptions,
    sideBar: PxdAgGridSideBarOptions,
    columnTypes: PxdAgGridColumnTypes,
    groupDisplayType: 'groupRows',
    //groupUseEntireRow: true,
    masterDetail: true,
    isRowMaster: (dataItem: ProjectEstimateDetailItemDto) => {
      if (dataItem.DependentScopingItems && dataItem.DependentScopingItems.length > 0) {
        return true;
      }
      return false;
    },
    detailRowAutoHeight: true,
    detailCellRendererParams: {
      detailGridOptions: {
        suppressCsvExport: true,
        suppressExcelExport: true,
        rowSelection: { mode: 'multiRow', headerCheckbox: true, selectAll: 'filtered', enableClickSelection: false },
        onCellValueChanged: (event: CellValueChangedEvent) => { this.onCellValueChanged(event) },
        columnDefs: [
          { field: 'ItemRateId', hide: true },
          { field: 'ProjectResourceItemType', headerName: 'Type' },
          { headerName: 'Sync Status', tooltipValueGetter: params => this.customToolTip(params), valueGetter: params => params.data ? SyncStatuses[params.data.SyncStatus] : '' },
          {
            field: 'Phase',
            editable: params => { return this.isEditable(params) },
            cellEditorSelector: params => {
              return {
                component: PxdAgGridSelectEditorComponent,
                params: {
                  value: params.data.Phase,
                  values: this.itemPhasesDistinct.map(x => { return { value: x, text: x } })
                }
              }
            },
            keyCreator: params => (params.value === null || params.value === '' ? 'NA' : params.value)
          },
          {
            field: 'Step',
            editable: params => { return this.isEditable(params) },
            cellEditorSelector: params => {
              return {
                component: PxdAgGridSelectEditorComponent,
                params: {
                  value: params.data.Step,
                  values: this.filterItemStepsForDropdown(params.data.Phase)
                }
              }
            },
            keyCreator: params => (params.value === null || params.value === '' ? 'NA' : params.value)
          },
          {
            field: 'PhaseOrder',
            hide: true
          },
          {
            field: 'StepOrder',
            hide: true
          },
          {
            field: 'ItemNumber',
            headerName: 'Item #'
          },
          {
            field: 'ItemDescription',
            headerName: 'Description'
          },
          {
            field: 'Quantity',
            editable: params => { return this.isEditable(params) },
            valueParser: params => round(Number(params.newValue), 2),
            type: 'numberColumn',
            tooltipField: 'EquationValue'
          },
          {
            field: 'UnitOfMeasureDescription',
            headerName: 'UOM'
          },
          {
            field: 'Rate',
            editable: params => { return this.isEditable(params) },
            headerName: 'Unit Rate',
            valueParser: params => Number(params.newValue),
            type: 'numberColumn',
            valueFormatter: params => this.currencyFormatter(params.data.Rate)
          },
          {
            field: 'ExtendedValue',
            headerName: 'Cost',
            valueFormatter: params => this.currencyFormatter(params.data.ExtendedValue)
          },
          { field: 'CostCodeValue', headerName: 'Cost Code' },
          { field: 'CostCodeDescription', headerName: 'Cost Code Description' },
          { field: 'CostTypeValue', headerName: 'Cost Type' },
          { field: 'CostTypeDescription', headerName: 'Cost Type Description' },
          {
            field: 'CostCodeDescription',
            colId: 'GFCM',
            headerName: 'GFCM',
            valueFormatter: params => (params.data?.CostCodeValue && params.data.CostCodeDescription) ? `${params.data.CostCodeValue} - ${params.data.CostCodeDescription}` : ''
          },
          {
            field: 'ItemScopingCategoryDescription',
            headerName: 'Item Scoping Category',
            keyCreator: params => (params.value === null || params.value === '' ? 'NA' : params.value),
            hide: true
          }
        ] as ColDef[],
        defaultColDef: {
          sortable: true,
          flex: 1,
          resizable: true
        },
        tooltipShowDelay: 0,
        columnTypes: PxdAgGridColumnTypes
      } as GridOptions,
      getDetailRowData: (params: any) => {
        if (params.data) {
          params.successCallback(params.data.DependentScopingItems);
        }
        if (params.node.parent && params.node.parent.parent) { // Get the root/header checkbox row node
          if (params.node.parent.isSelected() && params.node.parent.parent.isSelected()) {
            params.node.detailGridInfo.api.selectAll();
          } else {
            params.node.detailGridInfo.api.deselectAll();
          }
        }
      },
      refreshStrategy: 'rows'
    },
    isExternalFilterPresent: () => { return this.applyTaxAndContingencyFilter },
    doesExternalFilterPass: node => { return this.filterOutTaxAndContingency(node) },
    groupRowRenderer: 'agGroupCellRenderer',
    groupRowRendererParams: {
      innerRenderer: PxdAgGridCustomGroupCellRendererComponent,
      suppressCount: true,
      sortable: true
    },
    suppressCsvExport: true,
    columnDefs: [
      { field: 'ItemRateId', hide: true },
      { field: 'ProjectResourceItemType', headerName: 'Type' },
      { headerName: 'Sync Status', tooltipValueGetter: params => this.customToolTip(params), valueGetter: params => params.data ? SyncStatuses[params.data.SyncStatus] : '' },
      {
        field: 'Phase',
        rowGroup: true,
        editable: params => { return this.isEditable(params) },
        cellEditorSelector: params => {
          return {
            component: PxdAgGridSelectEditorComponent,
            params: {
              value: params.data.Phase,
              values: this.itemPhasesDistinct.map(x => { return { value: x, text: x } })
            }
          }
        },
        comparator: (valueA, valueB) => {
          const orderA = this.phaseOrderMap.get(valueA) ?? Number.MAX_SAFE_INTEGER;
          const orderB = this.phaseOrderMap.get(valueB) ?? Number.MAX_SAFE_INTEGER;
          return orderA - orderB;
        },
        keyCreator: params => (params.value === null || params.value === '' ? 'NA' : params.value)
      },
      {
        field: 'Step',
        rowGroup: true,
        editable: params => { return this.isEditable(params) },
        cellEditorSelector: params => {
          return {
            component: PxdAgGridSelectEditorComponent,
            params: {
              value: params.data.Step,
              values: this.filterItemStepsForDropdown(params.data.Phase)
            }
          }
        },
        comparator: (valueA, valueB, nodeA, nodeB) => {
          const phaseA = nodeA?.data?.Phase ?? nodeA?.parent?.key ?? '';
          const phaseB = nodeB?.data?.Phase ?? nodeB?.parent?.key ?? '';
          const orderA = this.distinctPhaseStepMap?.get(`${phaseA}|${valueA}`)?.SortOrder ?? Number.MAX_SAFE_INTEGER;
          const orderB = this.distinctPhaseStepMap?.get(`${phaseB}|${valueB}`)?.SortOrder ?? Number.MAX_SAFE_INTEGER;
          return orderA - orderB;
        },
        keyCreator: params => (params.value === null || params.value === '' ? 'NA' : params.value)
      },
      {
        field: 'PhaseOrder',
        hide: true
      },
      {
        field: 'StepOrder',
        hide: true
      },
      {
        field: 'ItemNumber',
        headerName: 'Item #'
      },
      {
        field: 'ItemDescription',
        headerName: 'Description',
        editable: params => params.data.ProjectResourceItemTypeId === ProjectResourceItemType.ManualCost,
        valueParser: params => String(params.newValue),
        cellEditorParams: {
          maxLength: 200
        }
      },
      {
        field: 'Quantity',
        editable: params => { return this.isEditable(params) },
        valueParser: params => round(Number(params.newValue), 2),
        type: 'numberColumn',
        tooltipField: 'EquationValue'
      },
      {
        field: 'UnitOfMeasureDescription',
        headerName: 'UOM',
        editable: params => {
          return params.data.ProjectResourceItemTypeId === ProjectResourceItemType.ManualCost ? true : false
        },
        valueGetter: params => {
          if (params.data.ProjectResourceItemTypeId === ProjectResourceItemType.ManualCost) {
            return this.getIUnitOfMeasureDtosById(params.data.UnitOfMeasureId)
          }
          return params.data.UnitOfMeasureDescription;
        },
        valueSetter: (params) => {
          const selectedId = params.newValue;
          const selectedOption = this.IUnitOfMeasureDtos.find(option => option.UnitOfMeasureId === selectedId);

          if (selectedOption) {
            params.data.UnitOfMeasureId = selectedId;
            params.data.UnitOfMeasureDescription = selectedOption.Description
            return true;
          }
          return false;
        },
        width: 150,
        cellEditorSelector: params => {
          if (params.data.ProjectResourceItemTypeId === ProjectResourceItemType.ManualCost) {
            return {
              component: PxdAgGridSelectEditorComponent,
              params: {
                values: Array.from(this.IUnitOfMeasureDtos).map(d => {
                  return {
                    value: d.UnitOfMeasureId,
                    text: d.Description
                  };
                }),
                value: params.data.UnitOfMeasureDescription,
              }
            };
          }
          return null;
        }
      },
      {
        field: 'Rate',
        editable: params => { return this.isEditable(params) },
        headerName: 'Unit Rate',
        valueParser: params => Number(params.newValue),
        type: 'numberColumn',
        valueFormatter: params => this.currencyFormatter(params.data.Rate)
      },
      {
        field: 'ExtendedValue',
        headerName: 'Cost',
        valueFormatter: params => this.currencyFormatter(params.data.ExtendedValue)
      },
      {
        field: 'Tax',
        headerName: 'Tax',
        valueGetter: params =>
          this.roundToTwoDecimals(
            this.projectEstimate.TaxControl * params.data.ExtendedValue
          ),
        valueFormatter: params =>
          this.currencyFormatter(params.value)
      },
      {
        field: 'Contingency',
        headerName: 'Contingency',
        valueGetter: params =>
          this.roundToTwoDecimals(
            this.projectEstimate.ContingencyControl * params.data.ExtendedValue
          ),
        valueFormatter: params =>
          this.currencyFormatter(params.value)
      },
      {
        field: 'TotalCost',
        headerName: 'Total Cost',
        valueGetter: params => {
          const base = params.data.ExtendedValue || 0;
          const tax = params.getValue('Tax') || 0;
          const contingency = params.getValue('Contingency') || 0;
          return this.roundToTwoDecimals(base + tax + contingency);
        },
        valueFormatter: params =>
          this.currencyFormatter(params.value)
      },
      { field: 'CostCodeValue', headerName: 'Cost Code' },
      { field: 'CostCodeDescription', headerName: 'Cost Code Description' },
      { field: 'CostTypeValue', headerName: 'Cost Type' },
      { field: 'CostTypeDescription', headerName: 'Cost Type Description' },
      {
        field: 'CostCodeDescription',
        colId: 'GFCM',
        headerName: 'GFCM',
        valueFormatter: params => (params.data?.CostCodeValue && params.data.CostCodeDescription) ? `${params.data.CostCodeValue} - ${params.data.CostCodeDescription}` : '',
        keyCreator: params => (params.data?.CostCodeValue && params.data.CostCodeDescription) ? `${params.data.CostCodeValue} - ${params.data.CostCodeDescription}` : '',
        filterValueGetter: params => (params.data?.CostCodeValue && params.data.CostCodeDescription) ? `${params.data.CostCodeValue} - ${params.data.CostCodeDescription}` : '',
        filterParams: {
          valueFormatter: params => params.value,
        },
      },
      {
        field: 'ItemScopingCategoryDescription',
        headerName: 'Item Scoping Category',
        keyCreator: params => (params.value === null || params.value === '' ? 'NA' : params.value),
        hide: true,
        cellRenderer: 'agGroupCellRenderer'
      }
    ]
  }
  private dialogMode: DialogMode

  public estimateStatus: string = '';
  private projectSubTypeId: number;
  public projectEstimate: ProjectEstimate;

  readonly estimateStatusId = model<number>(undefined);
  readonly iProjectId = input<number>(undefined);
  readonly iProjectTypeId = input<number>(undefined);
  readonly iProjectEstimateId = input<number>(undefined);
  readonly projectEstimateState = input<EstimateState>(undefined);
  readonly iProjectStatusId = input<number>(undefined);
  readonly iRecordTypeId = input<number>(undefined);
  readonly dialogClosed = output<IDialogClosed>();

  xAfeActionButtonData: Array<any> = [
    {
      key: 'publishToExecute',
      text: 'Publish Snapshot',
      svgIcon: this.icons.publish,
      disabled: true,
      click: () => { this.onClickPublishToExecute(); }
    },
    {
      key: 'createAfeInExecute',
      text: 'Create WBS',
      svgIcon: this.icons.plusCircle,
      disabled: true,
      click: () => { this.onClickCreateAfeInExecute(); }
    },
    {
      key: 'updateAfeInExecute',
      text: 'Update WBS',
      svgIcon: this.icons.plusCircle,
      disabled: true,
      click: () => { this.onClickUpdateAfeInExecute(); }
    }
  ];

  addButtonData: Array<any> = [
    {
      key: 'items',
      svgIcon: this.icons.plusCircle,
      disabled: true,
      text: 'Items',
      click: () => { this.isItemsDialogOpen = true; }
    },
    {
      key: 'costs',
      svgIcon: this.icons.invoice,
      disabled: true,
      text: 'Costs',
      click: () => { this.isCcctDialogOpen = true; }
    }
  ];

  syncButtonData: Array<any> = [
    {
      key: 'QuantityAndRate',
      disabled: true,
      text: 'Quantity & Rate',
      click: () => { this.handleSyncDropDownSelection('QuantityAndRate'); }
    },
    {
      key: 'QuantityOnly',
      disabled: true,
      text: 'Quantity Only',
      click: () => { this.handleSyncDropDownSelection('QuantityOnly'); }
    },
    {
      key: 'RateOnly',
      disabled: true,
      text: 'Rate Only',
      click: () => { this.handleSyncDropDownSelection('RateOnly'); }
    }
  ];

  constructor() {
    this.projectEstimate = new ProjectEstimate();
    this.columnDefs = this.gridOptions.columnDefs;
    this.showEstimateCompareButton = this.environment.showEstimateCompareButton;
    //! For Dev Testing -- uncomment related code in the html to use
    // this.showDebugger = environment.envName === 'DEV' ? true : false;
  }

  //! For Dev Testing -- shows tax and cont. in grid to ensure good data
  // onToggleFilter() {
  //   this.applyTaxAndContingencyFilter = !this.applyTaxAndContingencyFilter;
  //   this.loadGrid();
  // }

  async ngOnInit() {
    this.isDraftEstimate = this.estimateStatusId() === ProjectEstimateStatus.Draft ? true : false;
    this.estimateStatus = ProjectEstimateStatus[this.estimateStatusId()];
    this.projectSubTypeId = this.projectState.data.project.ProjectSubTypeId;
    this.showGeneratingEstimateDialog = true;
    this.generateHeaderForm();
    try {
      this.IUnitOfMeasureDtos = (await this.lookupService.findUnitOfMeasureDtos()).Results;
      await this.lookupPlusService.getLookupNoCount('AFEUpdateReasons').then(payload => {
        this.reasonsToUpdateAFE = payload.value;
      });
      await this.setPhaseStepData();
      await this.projectEstimateService.syncStandardConfiguration(this.iProjectId()).toPromise();
      await this.getEstimate(this.iProjectEstimateId());
      await this.getProjectItems();
      this.projectHeaderRefreshService.triggerHeaderRefresh();
    }
    finally {
      this.showGeneratingEstimateDialog = false;
      this.isFirstTimeLoading = false;
      this.cdr.detectChanges();
    }
    this.checkMissingPadConstructionWbs();
  }

  private async checkMissingPadConstructionWbs(): Promise<void> {
    try {
      const ssns: string[] = await this.lookupPlusService.getTypedObject<string[]>('projectdata', `getMissingPadConstructionWbs/${this.iProjectId()}`);
      if (ssns && ssns.length > 0) {
        this.missingPadConstructionWbsWarnings = ssns.map(ssn => `Warning: no Pad Construction WBS exists for this Well: ${ssn}`);
      } else {
        this.missingPadConstructionWbsWarnings = [];
      }
    } catch (error) {
      console.error('Error checking missing pad construction WBS:', error);
    }
    this.cdr.detectChanges();
  }

  async onGridReady(e: GridReadyEvent) {
    this.gridApi = e.api;
    this.applyAfeCostColumns();
  }

  private COST_COLS = new Set(['CostCodeValue', 'CostCodeDescription', 'CostTypeValue', 'CostTypeDescription']);
  private GFCM = 'GFCM';

  private transformDefs(defs: (ColDef | ColGroupDef)[] | undefined, can: boolean): (ColDef | ColGroupDef)[] | undefined {
    if (!defs) return defs;
    return defs.map(def => {
      if ('children' in def) return def;
      const id = def.colId ?? def.field;
      if (!id) return def;

      const hide =
        this.COST_COLS.has(id) ? can :
          id === this.GFCM ? !can :
            def.hide;

      const suppress = this.COST_COLS.has(id) ? can : (id === this.GFCM ? !can : undefined);

      return {
        ...def,
        ...(hide !== undefined ? { hide } : null),
        ...(suppress !== undefined ? {
          suppressColumnsToolPanel: suppress,
          suppressFiltersToolPanel: suppress,
        } : null),
      };
    });
  }

  private applyVisibility(api: GridApi, can: boolean) {
    const defs = api.getColumnDefs() as (ColDef | ColGroupDef)[];
    api.setGridOption('columnDefs', this.transformDefs(defs, can));

    api.setColumnsVisible(Array.from(this.COST_COLS), !can);
    api.setColumnsVisible([this.GFCM], can);

    const toClear = can ? Array.from(this.COST_COLS) : [this.GFCM];
    toClear.forEach(col => api.setColumnFilterModel(col, null));
    api.onFilterChanged();
  }

  private applyAfeCostColumns(): void {
    if (!this.gridApi) return;

    this.applyVisibility(this.gridApi, true);

    const dcrp: any = this.gridApi.getGridOption('detailCellRendererParams') || {};
    const dgo = dcrp.detailGridOptions || {};
    const baseDetailDefs = (dgo.columnDefs as (ColDef | ColGroupDef)[] | undefined)
      ?? (this.gridApi.getColumnDefs() as (ColDef | ColGroupDef)[]);
    this.gridApi.setGridOption('detailCellRendererParams', {
      ...dcrp,
      detailGridOptions: {
        ...dgo,
        columnDefs: this.transformDefs(baseDetailDefs, true),
      },
    });
  }

  private findPhaseStepTypeIdFromKey(phase: string, step: string): number | null {
    let key = this.phaseStepKeyMaker({ Phase: phase, Step: step });
    let keydPhaseStepSort = this.distinctPhaseStep.find(x => x.PSKey === key);
    if (keydPhaseStepSort) {
      return keydPhaseStepSort.PhaseStepTypeId;
    }
    return null;
  }

  async onCellValueChanged(e: any) {
    if (e.oldValue == e.newValue) {
      return;
    }
    if (e.colDef.field === 'Phase') {
      //TODO Should this also "Clear" out the step so its more obvious?
      e.data.Step = "";
      let stepColumn = this.gridApi.getAllGridColumns().find(x => x.getColDef().field === 'Step');
      this.gridApi.startEditingCell({ rowIndex: e.rowIndex, colKey: stepColumn });
      return;
    }
    if (e.colDef.field === 'Step') {
      e.data.PhaseStepTypeId = this.findPhaseStepTypeIdFromKey(e.data.Phase, e.data.Step);
    }
    if (e.colDef.field === 'Quantity' && e.data.DependentScopingItems != null) {
      //? This will need to be updated when we upgrade ag grid to latest version :(
      if (e.node.detailNode && e.node.detailNode.detailGridInfo && e.node.detailNode.detailGridInfo.api) {
        e.node.detailNode.detailGridInfo.api.forEachNode(node => {
          node.data.Quantity = node.data.Quantity * e.data.Quantity;
          node.data.ExtendedValue = node.data.Quantity * node.data.Rate;

          e.node.detailNode.detailGridInfo.api.applyTransaction({ update: [node.data] });
          if ((node.data.ProjectResourceItemId > 0 && node.data.ProjectCostCodeCostTypeItemId === null)
            || (node.data.ProjectResourceItemId === null && node.data.ProjectCostCodeCostTypeItemId > 0)) {
            this.userEditedItems.set(this.dtoIdKeyMaker(node.data.ProjectResourceItemId, node.data.ProjectCostCodeCostTypeItemId), node.data);
          }
        });
      }
    }
    e.data.ExtendedValue = e.data.Quantity * e.data.Rate;
    e.api.applyTransaction({ update: [e.data] });
    if ((e.data.ProjectResourceItemId > 0 && e.data.ProjectCostCodeCostTypeItemId === null)
      || (e.data.ProjectResourceItemId === null && e.data.ProjectCostCodeCostTypeItemId > 0)) {
      this.userEditedItems.set(this.dtoIdKeyMaker(e.data.ProjectResourceItemId, e.data.ProjectCostCodeCostTypeItemId), e.data);
    }
    this.onComponentValuesChanged();
  }

  private dtoIdKeyMaker(projectResourceItemId: number | null, projectCostCodeCostTypeItemId: number | null): string {
    return `ProjectResourceItemId:${projectResourceItemId},ProjectCostCodeCostTypeItemId:${projectCostCodeCostTypeItemId}`;
  }

  private findDuplicate(list: ProjectEstimateDetailItemDto[]) {
    const SEP = '\u001F';

    const display = (v: string | undefined | null) => {
      return v?.trim().toLowerCase() ?? '';
    };
    const key = (o: ProjectEstimateDetailItemDto) =>
      `${o.ItemNumber}${SEP}${display(o.Phase)}${SEP}${display(o.Step)}${SEP}${o.CostCodeValue}`;

    const seen = new Map<string, ProjectEstimateDetailItemDto>();
    const duplicates: DuplicateInfo[] = [];

    for (const item of list) {
      const k = key(item);

      if (seen.has(k)) {
        duplicates.push({
          ItemNumber: item.ItemNumber,
          Phase: display(item.Phase),
          Step: display(item.Step),
          GFCMCode: item.CostCodeValue
        });
      } else {
        seen.set(k, item);
      }
    }

    return {
      hasDuplicate: duplicates.length > 0,
      duplicateItems: duplicates
    };
  }
  async onSave() {
    let toDeleteArray = Array.from(this.userDeletedItems.values());
    let toUpdateArray = Array.from(this.userEditedItems.values());
    let toCreateArray = this.rowData.filter(x => (x.ProjectResourceItemId <= 0 && x.ProjectCostCodeCostTypeItemId === null) || (x.ProjectCostCodeCostTypeItemId <= 0 && x.ProjectResourceItemId === null));

    const alreadyAddedItems = this.findDuplicate(this.rowData.filter(x => x.ProjectResourceItemId != null));
    let duplicates: DuplicateInfo[] = [];

    if (alreadyAddedItems?.hasDuplicate) {
      duplicates.push(...alreadyAddedItems.duplicateItems);
    }

    const alreadyAddedCostItems = this.findDuplicate(this.rowData.filter(x => x.ProjectCostCodeCostTypeItemId != null && x.CostCodeCostTypeId !== GlobalCcctCombination.Tax && x.CostCodeCostTypeId !== GlobalCcctCombination.Contingency));
    if (alreadyAddedCostItems?.hasDuplicate) {
      duplicates.push(...alreadyAddedCostItems.duplicateItems);
    }
    if (duplicates.length > 0) {
      this.hasEstimateDuplicateItems = true;
      this.duplicateEstimateItems = duplicates.slice(0, 4)
        .map(d =>
          `Item: ${d.ItemNumber}, GFCM: ${d.GFCMCode}, Phase: ${d.Phase}, Step: ${d.Step};`
        );
      return;
    }
    else {
      this.hasEstimateDuplicateItems = false;
      this.duplicateEstimateItems = [];
    }

    toDeleteArray = toDeleteArray.map(x => {
      return {
        ProjectResourceItemId: x.ProjectResourceItemId,
        ProjectCostCodeCostTypeItemId: x.ProjectCostCodeCostTypeItemId
      } as ProjectEstimateDetailItemDto
    });

    toUpdateArray = toUpdateArray.map(x => {
      return {
        ProjectResourceItemId: x.ProjectResourceItemId,
        ProjectCostCodeCostTypeItemId: x.ProjectCostCodeCostTypeItemId,
        Rate: x.Rate,
        ItemDescription: x.ItemDescription,
        UnitOfMeasureId: x.UnitOfMeasureId,
        Quantity: x.Quantity,
        ExtendedValue: x.ExtendedValue,
        Phase: x.Phase,
        Step: x.Step,
        PhaseStepTypeId: x.PhaseStepTypeId
      } as ProjectEstimateDetailItemDto
    });

    //TODO in Future.... Add validation to ensure phasesteop typeid matches the Phase and step combination selected

    let taxCost = this.projectEstimate.TotalTax;
    let contCost = this.projectEstimate.TotalContingency;
    let taxCCCTItem: any = {
      ProjectResourceItemId: null,
      ProjectCostCodeCostTypeItemId: this.projectTaxCcctItemId,
      Rate: taxCost,
      Quantity: 1,
      ExtendedValue: taxCost
    } as ProjectEstimateDetailItemDto;
    toUpdateArray.push(taxCCCTItem);
    let contingencyCCCTItem: any = {
      ProjectResourceItemId: null,
      ProjectCostCodeCostTypeItemId: this.projectContingencyCcctItemId,
      Rate: contCost,
      Quantity: 1,
      ExtendedValue: contCost
    } as ProjectEstimateDetailItemDto;
    toUpdateArray.push(contingencyCCCTItem);

    try {
      this.disableAllButtons = true;
      this.saving = true;

      await this.projectEstimateDetailService.UpsertProjectItem({ ProjectEstimateId: this.iProjectEstimateId(), ToCreate: toCreateArray, ToDelete: toDeleteArray, ToUpdate: toUpdateArray });
      await this.updateEstimate();
      this.saving = false;
      this.cdr.detectChanges();
      await this.getProjectItems();
      this.cleanHeaderForm();
      this.disableAllButtons = false;
      this.cdr.detectChanges();
    } catch (err) {
      this.errorService.handleHTTPErrorWithMessage(err, "Error Saving Project Estimate");
      await this.getProjectItems();
      this.cleanHeaderForm();
      this.saving = false;
      this.disableAllButtons = false;
      this.cdr.detectChanges();
    }
  }

  async onCancel() {
    this.userDeletedItems = new Map<string, ProjectEstimateDetailItemDto>();
    this.userEditedItems = new Map<string, ProjectEstimateDetailItemDto>();
    this.hasPendingChange = false;
    this.newItemId = 0;
    this.disableAllButtons = true;
    await this.getEstimate(this.iProjectEstimateId());
    await this.getProjectItems();
    this.cleanHeaderForm();
    this.disableAllButtons = false;
    this.cdr.detectChanges();
  }

  private cleanHeaderForm() {
    this.headerForm.reset({
      taxControl: this.projectEstimate.TaxControl,
      contingencyControl: this.projectEstimate.ContingencyControl,
      taxCostControl: this.projectEstimate.TaxCostControl,
      contingencyCostControl: this.projectEstimate.ContingencyCostControl
    });
  }

  private phaseStepKeyMaker = (item: any) => `${item.Phase}|${item.Step}`
  public hasNoSchedule = false;
  public hasMissingPadConstructionWbs = false;
  public missingPadConstructionWbsWarnings: string[] = [];

  private async setPhaseStepData(): Promise<void> {
    // get scheduleId for this project
    const url1: string = `Projects(${this.iProjectId()})?$select=ScheduleId`;
    let payload = await this.lookupPlusService.getLookupCustom(url1);
    let scheduleId: number = payload.ScheduleId;

    if (!scheduleId) {
      this.errorService.error('Schedule not loaded for project. The Project will not function correctly without a schedule');
      this.distinctPhaseStepMap = new Map();
      this.hasNoSchedule = true;
      return;
    }

    // get phase and steps from schedule activities table based off schedule id
    const url2: string = `ScheduleActivities?$filter=ScheduleId%20eq%20${scheduleId}&$expand=PhaseStepType($select=Phase,Step,SortOrder,PhaseStepTypeId)&$select=PhaseStepType`;
    this.itemPhaseStepTypes = (await this.lookupPlusService.getLookupCustom(url2)).value.map(x => x.PhaseStepType)
    this.itemPhaseStepTypes.sort((a, b) => (a.SortOrder > b.SortOrder) ? 1 : -1); //sort by sortOrder
    this.itemPhasesDistinct = Array.from(new Set(this.itemPhaseStepTypes.map(x => x.Phase)));

    //uses the SCHEDULE's phases and steps instead of only already associated ones
    this.distinctPhaseStep = this.unique(this.itemPhaseStepTypes.filter(x => x.Phase !== null && x.Phase !== ''), ['Phase', 'Step', 'SortOrder'])
      .map((item: PhaseStepType) => {
        return {
          PSKey: this.phaseStepKeyMaker(item),
          PhaseStepTypeId: item.PhaseStepTypeId,
          Phase: item.Phase,
          Step: item.Step,
          SortOrder: item.SortOrder,
          PhaseOrder: 0,
          StepOrder: 0
        };
      });

    //set phaseorder and step order
    if (this.distinctPhaseStep.length == 0) {
      this.errorService.error(`No phases found for Schedule with Id ${scheduleId} associated with this Project. The Project will not function correctly without a valid schedule`);
      this.distinctPhaseStepMap = new Map();
      this.hasNoSchedule = true;
      return;
    }
    this.hasNoSchedule = false;

    let phase = this.distinctPhaseStep[0].Phase;
    let step = this.distinctPhaseStep[0].Step;
    let phaseOrder = 1;
    let stepOrder = 1;

    //grouping by Phase, each new phase gets a new phase order, then each step under that phase gets a new step order, reset at 0 for new phases
    this.distinctPhaseStep.forEach((x) => {
      if (x.Phase !== phase) {
        phaseOrder++;
        stepOrder = 1;
        phase = x.Phase;
      }
      if (x.Step !== step) {
        stepOrder++;
        step = x.Step;
      }
      x.PhaseOrder = phaseOrder;
      x.StepOrder = phaseOrder + stepOrder / 100.0;   //1.00, 1.01, 1.02 ... 2.01, 2.02, 2.03, ...
    });
    this.distinctPhaseStepMap = new Map(this.distinctPhaseStep.map(i => [i.PSKey, i]));

    // Build phase order map for group sorting: CONST first, then by min SortOrder
    this.phaseOrderMap = new Map();
    this.distinctPhaseStep.forEach(x => {
      const candidate = x.Phase === 'CONST' ? -1 : (x.SortOrder ?? Number.MAX_SAFE_INTEGER);
      const existing = this.phaseOrderMap.get(x.Phase);
      if (existing === undefined || candidate < existing) {
        this.phaseOrderMap.set(x.Phase, candidate);
      }
    });
  }

  closeDialog() {
    this.isCcctDialogOpen = false;
    this.isItemsDialogOpen = false;
  }

  getIUnitOfMeasureDtosById = (id) => {
    if (!id) return this.defaultUnitOfMeasure;
    let atype = this.IUnitOfMeasureDtos.find(a => a.UnitOfMeasureId == id);
    if (atype) return atype.Description;
    return this.defaultUnitOfMeasure;
  }

  private async getEstimate(id: number): Promise<void> {
    let result: IProjectEstimate = await this.projectEstimateService.getEstimateByEstimateId(id);
    this.projectEstimate = new ProjectEstimate(result);
    this.onIsTaxPercentChanged(this.projectEstimate.IsTaxPercent);
    this.onIsContingencyPercentChanged(this.projectEstimate.IsContingencyPercent);
  }

  public async getProjectItems(): Promise<void> {
    let compareEstimate = this.isDraftEstimate ? true : false;
    let projectItems = await this.projectEstimateDetailService.GetProjectEstimateItems(this.projectEstimate.ProjectId, this.projectEstimate.EstimateVersion, this.projectEstimate.ProjectEstimateStatusId, compareEstimate);
    let results = projectItems.ProjectEstimateDetailItems.Results;

    if (this.isDraftEstimate) {
      this.newItems = projectItems.NewDraftEstimateItems;
      this.deletedItems = projectItems.DeletedDraftEstimateItems;
      this.updatedItems = projectItems.UpdatedDraftEstimateItems;
      this.inactiveItems = projectItems.InactiveDraftEstimateItems;
    }
    this.calculateSubTotal(results);
    this.setItemPhaseStepOrderValues(results);
    this.rowData = results;

    if (this.iProjectTypeId() === ProjectTypeId.TankBattery) {
      this.nestRowData();
    }

    this.loadGrid();
    this.hasPendingChange = false;
    this.newItemId = 0;
    this.disableAllButtons = false;
    this.cdr.detectChanges();

    this.setGridGroupState();

    this.isFirstTimeLoading = false;
  }

  private setItemPhaseStepOrderValues(rowData: ProjectEstimateDetailItemDto[]) {
    rowData.forEach(item => {
      let key = this.phaseStepKeyMaker(item);
      let pst = this.distinctPhaseStepMap.get(key);
      if (pst) {
        // Match schedule tab sort: "CONST" phase first, then by SortOrder
        item.PhaseOrder = pst.Phase === 'CONST' ? 0 : 1;
        item.StepOrder = pst.SortOrder;
      }
    });
  }

  private loadGrid() {
    this.gridApi.setGridOption('rowData', this.rowData);

    const allColumnDefs = this.gridApi.getColumnDefs();
    const columnDefs = allColumnDefs.filter((col: any) => col.field && !['CostTypeValue', 'CostTypeDescription'].includes(col.field));

    if (this.isDraftEstimate) {
      // Look for new estimate items and pin them on top of the grid
      if (this.newItems.length > 0 && this.rowData.length > 2 /*there will always be a tax and contingency*/) {
        this.gridApi.setGridOption('pinnedTopRowData', [{ SyncStatus: SyncStatuses.StandardConfigHasNewItem }]);
      } else {
        // Remove the pinned items after estimate refresh
        this.gridApi.setGridOption('pinnedTopRowData', []);
      }
    } else {
      // Hide the SyncStatus column from the grid, columns / filters tool panel      
      columnDefs.forEach(function (colDef: ColDef) {
        if (colDef.headerName == 'Sync Status') {
          colDef.hide = true;
          colDef.suppressColumnsToolPanel = true;
          colDef.suppressFiltersToolPanel = true;
        }
      });
    }
    this.columnDefs = columnDefs;
  }

  private setGridGroupState() {
    if (this.isFirstTimeLoading) {
      if (this.iProjectTypeId() === ProjectTypeId.TankBattery) {
        this.gridApi.setColumnsVisible(['Step', 'Phase'], false);
        this.gridApi.setColumnsVisible(['ItemScopingCategoryDescription'], true);
        this.gridApi.moveColumns(['ItemScopingCategoryDescription'], 3);

      } else {
        this.gridApi.setColumnsVisible(['Step', 'Phase'], true);
      }

      if (this.iProjectTypeId() != ProjectTypeId.DrillOilGasWell &&
        this.iProjectTypeId() != ProjectTypeId.CompleteStimOAG &&
        this.iProjectTypeId() != ProjectTypeId.CompletePostStimOAG) {
        let groupedColumns = this.gridApi.getRowGroupColumns();
        this.gridApi.removeRowGroupColumns(groupedColumns);
        this.gridApi.setRowGroupColumns([]);
      }

      this.gridApi.applyColumnState({
        state: [{ colId: 'Phase', sort: 'asc', sortIndex: 1 }, { colId: 'Step', sort: 'asc', sortIndex: 2 }, { colId: 'ItemDescription', sort: 'asc', sortIndex: 3 }],
        defaultState: { sort: null },
      });
    }

    this.gridApi.expandAll();
    this.gridApi.autoSizeAllColumns();
  }

  private unique(arr: any, keyProps: any): any {
    const kvArray = arr.map(entry => {
      const key = keyProps.map(k => entry[k]).join('|');
      return [key, entry];
    });
    const map = new Map(kvArray);
    return Array.from(map.values());
  }

  private calculateSubTotal(items: ProjectEstimateDetailItemDto[]): void {
    let subTotal: number = 0;
    items.forEach(element => {
      element.ExtendedValue = element.Rate * element.Quantity;
      switch (element.CostCodeCostTypeId) {
        case eCostCodeCostTypes.Tax:
          this.projectTaxCcctItemId = element.ProjectCostCodeCostTypeItemId;
          //for export formatting
          element.ItemDescription = "Tax";
          element.UnitOfMeasureDescription = "";
          element.Phase = "";
          element.Step = "";
          break;
        case eCostCodeCostTypes.Contingency:
          this.projectContingencyCcctItemId = element.ProjectCostCodeCostTypeItemId;
          //for export formatting
          element.ItemDescription = "Contingency";
          element.UnitOfMeasureDescription = "";
          element.Phase = "";
          element.Step = "";
          break;
        default:
          if (element.ExtendedValue !== null && element.ExtendedValue !== undefined) {
            subTotal = subTotal + element.ExtendedValue;
          }
          break;
      }
    });
    this.projectEstimate.subTotalItemsAndCccts = subTotal;
  }

  addNewItems(ped: { items: IItemCostCodeCostTypeDto[], phaseSteps: string[] }) {
    let created: ProjectEstimateDetailItemDto[] = [];
    ped.items.forEach(e => {
      let pedi: ProjectEstimateDetailItemDto = {
        ProjectResourceItemId: this.newItemId--,
        ProjectCostCodeCostTypeItemId: null,
        ProjectResourceItemTypeId: ProjectResourceItemType.ManualItem,
        ProjectResourceItemType: ProjectResourceItemType[ProjectResourceItemType.ManualItem],
        EstimateVersion: this.projectEstimate.EstimateVersion,
        IsManualAdd: true,
        ExtendedValue: 0,
        Quantity: 1,
        Rate: 0,
        ProjectId: this.iProjectId(),
        ItemId: e.ItemId,
        ItemNumber: e.ItemNumber,
        ItemDescription: e.ItemShortDescription,
        Phase: '',
        Step: '',
        ParentItemId: null,
        UnitOfMeasureId: e.UnitOfMeasureId,
        UnitOfMeasureDescription: e.UOM,
        CostCodeCostTypeId: e.CostCodeCostTypeId,
        CostCodeValue: e.CostCodeValue,
        CostCodeDescription: e.CostCodeDescription,
      };
      if (this.IsDrillingOrCompletion() && ped.phaseSteps?.length) {
        ped.phaseSteps.forEach((phaseStep, index) => {
          const item: ProjectEstimateDetailItemDto = index === 0 ? pedi : { ...pedi };
          item.Phase = phaseStep.split('|')[0] ?? '';
          item.Step = phaseStep.split('|')[1] ?? '';
          item.PhaseStepTypeId = this.findPhaseStepTypeIdFromKey(item.Phase, item.Step);
          created.push(item);
        });
      } else {
        created.push(pedi);
      }
    });
    if (created.length <= 0) {
      this.closeDialog();
      return;
    }
    this.gridApi.applyTransaction({ add: created });
    this.rowData = this.rowData.concat(created);
    this.closeDialog();
    this.onComponentValuesChanged();
  }

  addNewCcctItem(ccct: { costItems: IProjectCostCodeCostTypeItem[], phaseSteps: string[] }) {
    let urlPhaseStep: string;
    let created: ProjectEstimateDetailItemDto[] = [];
    ccct.costItems.forEach(cc => {
      let pcccti: ProjectEstimateDetailItemDto = {
        ProjectResourceItemId: null,
        ProjectCostCodeCostTypeItemId: this.newItemId--,
        ProjectResourceItemTypeId: ProjectResourceItemType.ManualCost,
        ProjectResourceItemType: ProjectResourceItemType[ProjectResourceItemType.ManualCost],
        EstimateVersion: this.projectEstimate.EstimateVersion,
        IsManualAdd: true,
        ExtendedValue: 0,
        Quantity: 1,
        Rate: 0,
        ProjectId: this.iProjectId(),
        Phase: '',
        Step: '',
        ParentItemId: null,
        UnitOfMeasureId: 12,
        UnitOfMeasureDescription: 'Each',
        CostCodeCostTypeId: cc.CostCodeCostTypeId,
        CostCodeDescription: cc.CostCodeCostType.CostCode?.CostCodeDescription ?? '',
        CostCodeValue: cc.CostCodeCostType.CostCode?.CostCodeValue ?? 0,
        CostTypeDescription: cc.CostCodeCostType.CostType?.CostTypeDescription ?? '',
        CostTypeValue: cc.CostCodeCostType.CostType?.CostTypeValue ?? 0
      };
      if (this.IsDrillingOrCompletion() && ccct.phaseSteps?.length) {
        ccct.phaseSteps.forEach((phaseStep, index) => {
          const item: ProjectEstimateDetailItemDto = index === 0 ? pcccti : { ...pcccti };
          item.Phase = phaseStep.split('|')[0] ?? '';
          item.Step = phaseStep.split('|')[1] ?? '';
          item.PhaseStepTypeId = this.findPhaseStepTypeIdFromKey(item.Phase, item.Step);
          created.push(item);
        });
      } else {
        urlPhaseStep = `estimation/projects/${this.iProjectId()}/ccct/${cc.CostCodeCostTypeId}`;
        this.lookupPlusService.getLookupNoCount(urlPhaseStep)
          .then(xyz => {
            let phaseStep = Object.assign({}, xyz);
            pcccti.Phase = phaseStep.Phase;
            pcccti.Step = phaseStep.Step;
            pcccti.PhaseStepTypeId = this.findPhaseStepTypeIdFromKey(pcccti.Phase, pcccti.Step);
          });
        created.push(pcccti);
      }
    });
    if (created.length <= 0) {
      this.closeDialog();
      return;
    }
    this.gridApi.applyTransaction({ add: created });
    this.rowData = this.rowData.concat(created);
    this.closeDialog();
    this.onComponentValuesChanged();
  }

  public onDelete() {
    let parentRows = this.gridApi.getSelectedRows() as ProjectEstimateDetailItemDto[];
    let detailRows: ProjectEstimateDetailItemDto[] = [];
    this.gridApi.forEachDetailGridInfo(gridInfo => {
      detailRows = detailRows.concat(gridInfo.api.getSelectedRows())
    });
    let detailRowsOfParentRows = parentRows.map(x => x.DependentScopingItems).reduce((a, b) => a.concat(b), []).filter(x => x != null)
    let allSelectedRows = parentRows.concat(detailRows);
    let allRowsToDelete = allSelectedRows.concat(detailRowsOfParentRows);
    if (allRowsToDelete.length <= 0) {
      this.gridApi.deselectAll();
      return;
    }
    this.gridApi.applyTransaction({ remove: allSelectedRows });
    this.gridApi.deselectAll();
    allSelectedRows.forEach(x => {
      this.rowData = this.rowData.filter(y => (x.ProjectResourceItemId !== y.ProjectResourceItemId && x.ProjectCostCodeCostTypeItemId === null)
        || (x.ProjectResourceItemId === null && x.ProjectCostCodeCostTypeItemId !== y.ProjectCostCodeCostTypeItemId));
      this.userEditedItems.delete(this.dtoIdKeyMaker(x.ProjectResourceItemId, x.ProjectCostCodeCostTypeItemId));
    })
    //Only actually delete items that have a record
    this.userDeletedItems = new Map<string, ProjectEstimateDetailItemDto>();
    allRowsToDelete
      .filter(x => (x.ProjectResourceItemId > 0 && x.ProjectCostCodeCostTypeItemId === null)
        || (x.ProjectResourceItemId === null && x.ProjectCostCodeCostTypeItemId > 0))
      .forEach(x => {
        this.userDeletedItems.set(this.dtoIdKeyMaker(x.ProjectResourceItemId, x.ProjectCostCodeCostTypeItemId), x);
      });
    this.onComponentValuesChanged();
  }

  private onComponentValuesChanged() {
    this.hasPendingChange = true;
    this.flattenRowData();
    this.calculateSubTotal(this.rowData);
    this.nestRowData();
    this.cdr.detectChanges();
  }

  public generateHeaderForm(): void {
    this.headerForm = this.formBuilder.group({
      taxControl: new UntypedFormControl({ value: this.projectEstimate.TaxControl, disabled: !this.isDraftEstimate || !this.projectEstimate.IsTaxPercent }),
      contingencyControl: new UntypedFormControl({ value: this.projectEstimate.ContingencyControl, disabled: !this.isDraftEstimate || !this.projectEstimate.IsContingencyPercent }),
      taxCostControl: new UntypedFormControl({ value: this.projectEstimate.TaxCostControl, disabled: !this.isDraftEstimate || this.projectEstimate.IsTaxPercent }),
      contingencyCostControl: new UntypedFormControl({ value: this.projectEstimate.ContingencyCostControl, disabled: !this.isDraftEstimate || this.projectEstimate.IsContingencyPercent })
    });
    this.headerForm.valueChanges.subscribe(data => this.onFormValueChanged(data));
  }

  onIsTaxPercentChanged(isTaxPercentEnabled: boolean) {
    if (!this.isDraftEstimate) return;
    this.projectEstimate.IsTaxPercent = isTaxPercentEnabled;
    this.hasPendingChange = true;
    if (isTaxPercentEnabled) {
      this.headerForm.controls.taxControl.enable({ emitEvent: false });
      this.headerForm.controls.taxCostControl.disable({ emitEvent: false });
    } else {
      this.headerForm.controls.taxControl.disable({ emitEvent: false });
      this.headerForm.controls.taxCostControl.enable({ emitEvent: false });
    }
  }

  onIsContingencyPercentChanged(isContingencyPercentEnabled: boolean) {
    if (!this.isDraftEstimate) return;
    this.projectEstimate.IsContingencyPercent = isContingencyPercentEnabled;
    this.hasPendingChange = true;

    if (isContingencyPercentEnabled) {
      this.headerForm.controls.contingencyControl.enable({ emitEvent: false });
      this.headerForm.controls.contingencyCostControl.disable({ emitEvent: false });
    } else {
      this.headerForm.controls.contingencyControl.disable({ emitEvent: false });
      this.headerForm.controls.contingencyCostControl.enable({ emitEvent: false });
    }
  }

  public onFormValueChanged(data?: any) {
    if (this.headerForm.dirty) {
      this.hasPendingChange = true;
      this.cdr.detectChanges();
    }
  }

  private updateProgressStep(step: string, success: boolean, errored: boolean, message: string,
    error: string[], processComplete: boolean = false, stepInProgress: boolean = true) {
    let msg: ProgressDialogMessage = null;
    msg = this.progressSteps.get(step);

    if (msg == null) {
      this.progressSteps.set(step, { IsSuccess: success, IsErrored: errored, InProgress: stepInProgress && (!processComplete && !errored), StepMessage: message })
    }
    else {
      msg.IsErrored = errored;
      msg.InProgress = false;
      msg.IsSuccess = success;
      msg.StepMessage = message;
      if (errored && error.length)
        this.progressDialog.ErrorMessage.push(...error);
      this.progressSteps.set(step, msg);
    }
    this.progressDialog.Progress = Array.from(this.progressSteps.values());
    this.processComplete = processComplete || errored;
    this.cdr.detectChanges();
  }

  private async runProjectValidation() {
    try {
      this.updateProgressStep('Validate', false, false, 'Validating project.', null);
      let validateUrl: string = this.getValidateUrl(this.iProjectId());
      this.projectValidateObject = await this.projectEstimateService.generalGet(validateUrl);
      if ((this.projectValidateObject.IsValid === false) && (this.projectValidateObject.ErrorMessages.length > 0)) {
        this.updateProgressStep('Validate', false, true, 'Error validating project', this.projectValidateObject.ErrorMessages);
        return;
      }

      this.updateProgressStep('Validate', true, false, 'Project validated successfully.', null);
    }
    catch (err) {
      this.updateProgressStep('Validate', false, true, 'Error validating project', [err]);
    }
  }

  private getValidateUrl(projectId: number): string {
    let validateUrl: string = `projectheader/validate?projectId=${projectId}&projectEstimateId=${this.iProjectEstimateId()}`;
    return validateUrl;
  }

  public async onClickPublishEstimate(): Promise<void> {
    this.progressDialog.DialogTitle = 'Publishing Project Estimate';
    this.progressDialog.Message = 'Project Publish request has been submitted and is currently being processed.';
    this.progressDialog.Progress = [];
    this.progressDialog.ErrorMessage = [];
    this.publishingProject = true;

    await this.onSave();
    await this.runProjectValidation();

    if (this.projectValidateObject.IsValid) {
      await this.publishDraftEstimate(true)
    }
  }

  private async estimatePublishComplete(publishonly: boolean = false, isLastStep: boolean = false) {
    this.topMessage = 'Estimate has been published from Draft.';
    this.showTopMessage = true;
    this.estimateStatusId.set(ProjectEstimateStatus.LatestPublished);
    this.isDraftEstimate = false;
    this.cdr.detectChanges();
    this.updateProgressStep('Publish', true, false, 'Project published successfully.', null, false);
    await this.createEstimateSnapshot(isLastStep);
    this.projectEstimate.ProjectEstimateStatus.Description = ProjectEstimateStatusDescription.LatestPublished;
    if (publishonly) {
      this.processComplete = true;
      this.cdr.detectChanges();
    }
  }

  public async createEstimateSnapshot(isLastStep: boolean = false) {
    // take snapshot of drilling/completion parameters against projectEstimateId
    try {
      const iProjectTypeId = this.iProjectTypeId();
      if (iProjectTypeId === ProjectTypeId.DrillOilGasWell || iProjectTypeId === ProjectTypeId.CompleteOilGasWell ||
        iProjectTypeId === ProjectTypeId.CompleteStimOAG || iProjectTypeId === ProjectTypeId.CompletePostStimOAG) {
        let isDrilling = iProjectTypeId === ProjectTypeId.DrillOilGasWell ? true : false;
        let url2 = `estimation/projects/${this.iProjectId()}/${isDrilling}/${this.iProjectEstimateId()}/snapshot`;
        this.updateProgressStep('Snapshot', false, false, 'Creating project estimate snapshot.', null);

        await this.projectCustomEndpointService.createSnapshot(url2);

        this.updateProgressStep('Snapshot', true, false, 'Project estimate snapshot created successfully.', null, isLastStep);
        return;
      } else {
        this.updateProgressStep('Snapshot', true, false, 'No Snapshot created (not used for this project type).', null, isLastStep, false);
      }
    }
    catch (err) {
      this.updateProgressStep('Snapshot', false, true, 'Error creating project estimate snapshot.', [err]);
    }
    return;
  }

  public async onClickPublishToExecute(): Promise<void> {
    this.progressDialog.DialogTitle = 'Publishing Snapshot';
    this.progressDialog.Message = 'Publish Snapshot request has been submitted and is currently being processed.';
    this.progressDialog.Progress = [];
    this.progressDialog.ErrorMessage = [];
    this.publishingProject = true;

    await this.onSave();
    await this.runProjectValidation();

    if (this.projectValidateObject.IsValid) {
      await this.publishDraftEstimate(true)
    }
  }

  public async onClickCreateAfeInExecute(): Promise<void> {
    this.progressDialog.DialogTitle = 'Creating WBS';
    this.progressDialog.Message = 'Create WBS request has been submitted and is currently being processed.';
    this.progressDialog.Progress = [];
    this.progressDialog.ErrorMessage = [];
    this.publishingProject = true;
    this.dialogMode = DIALOG_MODE.AFE;
    await this.runProjectValidation();

    if (this.projectValidateObject.IsValid) {
      if (this.isDraftEstimate) {
        await this.publishDraftEstimate(false);
      }
      if (this.estimateStatusId() === ProjectEstimateStatus.LatestPublished) {
        this.selectedReasonId = 0;
        await this.createAfeInExecute(this.selectedReasonId);
      }
    }
  }

  public async onClickUpdateAfeInExecute(): Promise<void> {
    this.showUpdateAFEDialog = true;
  }

  public async continueToUpdateAFE() {
    if (this.selectedReasonId > 0) {
      this.showUpdateAFEDialog = false;
      this.progressDialog.DialogTitle = 'Update WBS';
      this.progressDialog.Message = 'Update WBS request has been submitted and is currently being processed.';
      this.progressDialog.Progress = [];
      this.progressDialog.ErrorMessage = [];
      this.publishingProject = true;
      this.dialogMode = DIALOG_MODE.AFE;

      await this.runProjectValidation();

      if (this.projectValidateObject.IsValid) {
        if (this.isDraftEstimate) {
          await this.publishDraftEstimate(false);
        }
        await this.updateAfeInExecute(this.selectedReasonId);
      }
    }
  }

  xAfeActionButtonLogic(): void {
    if (this.disableAllButtons || this.projectState.data.status === 'Closed' || this.projectState.data.status === 'Cancelled') {
      this.xAfeActionButtonData.forEach(f => f.disabled = true);
      return;
    }

    // Determine "Publish Snapshot" button logic
    if (this.projectEstimate.EstimateTotal === 0 || this.publishingProject === true || !this.isDraftEstimate || this.hasNoSchedule || this.hasPendingChange) {
      this.xAfeActionButtonData.find(f => f.key === 'publishToExecute').disabled = true;
    } else {
      this.xAfeActionButtonData.find(f => f.key === 'publishToExecute').disabled = false;
    }

    // Draft estimate logic
    if (this.projectState.data.status === ProjectStatusDescription.Draft) {
      // Determine "Create AFE" button logic
      const projectEstimateState = this.projectEstimateState();
      if (this.hasNoSchedule || projectEstimateState.HasActiveAfe || projectEstimateState.HasApprovedAfe
        || projectEstimateState.HasActiveAfeRevision || projectEstimateState.HasApprovedAfeRevision
        || this.projectEstimate.EstimateTotal == 0 || this.hasPendingChange || projectEstimateState.HasAfeRecord) {
        this.xAfeActionButtonData.find(f => f.key === 'createAfeInExecute').disabled = true;
      } else {
        this.xAfeActionButtonData.find(f => f.key === 'createAfeInExecute').disabled = false;
      }

      // Determine "Update AFE" button logic
      if (!this.hasNoSchedule && projectEstimateState.HasApprovedAfe && !projectEstimateState.HasActiveAfeRevision && !this.hasPendingChange) {
        this.xAfeActionButtonData.find(f => f.key === 'updateAfeInExecute').disabled = false;
      } else {
        this.xAfeActionButtonData.find(f => f.key === 'updateAfeInExecute').disabled = true;
      }
    }
    // Button Logic for Revisions Estimate
    else {
      // Determine "Create AFE" button logic
      const projectEstimateState = this.projectEstimateState();
      const iRecordTypeId = this.iRecordTypeId();
      if (this.hasNoSchedule || projectEstimateState.HasActiveAfe || projectEstimateState.HasApprovedAfe
        || iRecordTypeId !== null || this.hasPendingChange || projectEstimateState.HasAfeRecord) {
        this.xAfeActionButtonData.find(f => f.key === 'createAfeInExecute').disabled = true;
      } else {
        this.xAfeActionButtonData.find(f => f.key === 'createAfeInExecute').disabled = false;
      }

      // Determine "Update AFE" button logic
      if ((!this.hasNoSchedule && projectEstimateState.HasApprovedAfe || projectEstimateState.HasApprovedAfeRevision)
        && iRecordTypeId === null && !projectEstimateState.HasActiveAfeRevision && !this.hasPendingChange) {
        this.xAfeActionButtonData.find(f => f.key === 'updateAfeInExecute').disabled = false;
      } else {
        this.xAfeActionButtonData.find(f => f.key === 'updateAfeInExecute').disabled = true;
      }
    }
  }

  private async publishDraftEstimate(isLastStep: boolean = false) {
    try {
      this.updateProgressStep('Publish', false, false, 'Publishing project.', null);
      let url = `estimation/projects/${this.iProjectId()}/publishestimate`;
      await this.projectCustomEndpointService.publishDraft(url);
      await this.estimatePublishComplete(isLastStep);
    } catch (e) {
      this.errorService.handleHTTPError(e).subscribe(res => { },
        e => {
          this.updateProgressStep('Publish', false, true, 'Error Publishing Project', [e]);
        });
    }
  }

  public onProgressMessageClose(): void {
    this.publishingProject = false;
    this.progressDialog.Progress = [];
    this.progressDialog.ErrorMessage = [];
    this.progressSteps = new Map<string, ProgressDialogMessage>();
    this.cdr.detectChanges();

    if (this.dialogMode == DIALOG_MODE.AFE) {
      this.dialogClosed.emit({ mode: DIALOG_MODE.AFE });
      this.dialogMode = null;
    }
  }

  protected VerifyPublishedBeforeCreate(label: string, actionName: string): boolean {
    if (this.estimateStatusId() !== ProjectEstimateStatus.LatestPublished) {
      this.updateProgressStep(
        label,
        false,
        false,
        `Cannot ${actionName}, estimate status: '${this.projectEstimate.ProjectEstimateStatus.Description}' but MUST be: 'Latest Published'`,
        null, true);
      return false;
    }
    return true;
  }

  public async createAfeInExecute(reasonId: number) {
    if (this.VerifyPublishedBeforeCreate('CreateWBS', 'Create WBS')) {
      let url: string = `estimation/executeAfe/${this.iProjectEstimateId()}/afetype/1/reason/${reasonId}`;
      await this.createExecuteRecord('CreateWBS', 'Creating WBS - A WBS is being created in Execute.', 'WBS created successfully.', url, 'HasAfeRecord');
      this.projectHeaderRefreshService.triggerHeaderRefresh();
    }
  }

  public async updateAfeInExecute(reasonId: number) {
    if (this.VerifyPublishedBeforeCreate('UpdateWBS', 'Update WBS')) {
      let url: string = `estimation/executeAfe/${this.iProjectEstimateId()}/afetype/3/reason/${reasonId}`;
      await this.createExecuteRecord('UpdateWBS', 'Updating WBS in Execute.', 'WBS updated successfully.', url, 'HasAfeRevRecord');
      this.projectHeaderRefreshService.triggerHeaderRefresh();
    }
  }

  public async createExecuteRecord(label: string, startMessage: string, endMessage: string, url: string, stateField: string): Promise<void> {
    this.updateProgressStep(label, false, false, startMessage, null);
    try {
      await this.lookupPlusService.getLookupNoCount(url);
      this.projectEstimateState()[stateField] = true;
      this.updateProgressStep(label, true, false, endMessage, null, true);
    }
    catch (err) {
      this.updateProgressStep(label, false, true, 'Error In Process', [err.error.Message], true);
      this.errorService.handleHTTPError(err).subscribe(res => { }, e => this.updateProgressStep(label, false, true, 'Error In Process.', [err.error.Message], true));
    }
  }

  public async updateEstimate(): Promise<void> {
    let updatePacket: any = {
      Tax: this.projectEstimate.TaxControl,
      Contingency: this.projectEstimate.ContingencyControl,
      TaxCost: this.projectEstimate.TaxCostControl,
      ContingencyCost: this.projectEstimate.ContingencyCostControl,
      IsTaxPercent: this.projectEstimate.IsTaxPercent,
      IsContingencyPercent: this.projectEstimate.IsContingencyPercent,
      EstimateTotal: this.projectEstimate.EstimateTotal
    };
    //  Get PATCH request in form that's usable with the api
    let Form = JSON.stringify(updatePacket);
    await this.projectEstimateService.updateEstimate(this.iProjectEstimateId(), Form);
  }

  addActionButtonLogic(): void {
    if (this.publishingProject || this.disableAllButtons) {
      this.addButtonData.find(f => f.key === 'items').disabled = true;
      this.addButtonData.find(f => f.key === 'costs').disabled = true;
    } else {
      this.addButtonData.find(f => f.key === 'items').disabled = false;
      this.addButtonData.find(f => f.key === 'costs').disabled = false;
    }
  }

  addSyncButtonLogic(): void {
    if (this.publishingProject || this.disableAllButtons || this.hasPendingChange) {
      this.syncButtonData.forEach(f => f.disabled = true);
    } else {
      this.syncButtonData.forEach(f => f.disabled = false);
    }
  }

  public async refreshEstimate(): Promise<void> {
    this.disableAllButtons = true;
    this.saving = true;
    var engineVersion = 3; // The Estimate Engine Version is v3 and we always use v3 now
    try {
      await this.projectEstimateService.startEstimateEngine(this.iProjectId(), engineVersion);
      await this.getProjectItems();
    }
    finally {
      this.saving = false;
      this.disableAllButtons = false;
      this.cdr.detectChanges();
    }
  }

  public async compareEstimate(): Promise<void> {
    this.disableAllButtons = true;
    await this.getProjectItems();
    this.disableAllButtons = false;
    this.cdr.detectChanges();
  }

  public async syncEstimateItems(toCreateArray, toDeleteArray, toUpdateArray, manualItemsToDelete): Promise<void> {
    this.disableAllButtons = true;
    this.saving = true;
    let finalItemsToSync = { ToCreate: toCreateArray, ToDelete: toDeleteArray, ToUpdate: toUpdateArray, ManualItemsToDelete: manualItemsToDelete };
    try {
      await this.projectEstimateService.startEstimateSync(this.iProjectId(), this.dropdownKey, finalItemsToSync);
      await this.getProjectItems();
    }
    finally {
      this.saving = false;
      this.disableAllButtons = false;
      this.cdr.detectChanges();
    }
  }

  public async onStdConfigChange(e): Promise<void> {
    this.showGeneratingEstimateDialog = true;
    try {
      await this.projectEstimateService.startEstimateEngine(this.iProjectId(), 3);
      await this.getProjectItems();
    }
    finally {
      this.projectHeaderRefreshService.triggerHeaderRefresh();
      this.showGeneratingEstimateDialog = false;
      this.cdr.detectChanges();
    }
  }

  public async runTabActivity(): Promise<void> {
    this.disableAllButtons = true;
    this.cdr.detectChanges();
    await this.getProjectItems();
    this.disableAllButtons = false;
    this.cdr.detectChanges();
  }

  public IsDrillingOrCompletion(): boolean {
    const iProjectTypeId = this.iProjectTypeId();
    if (iProjectTypeId === ProjectTypeId.DrillOilGasWell || iProjectTypeId === ProjectTypeId.CompleteOilGasWell ||
      iProjectTypeId === ProjectTypeId.CompleteStimOAG || iProjectTypeId === ProjectTypeId.CompletePostStimOAG) {
      if (this.projectSubTypeId === 25 || this.projectSubTypeId == 13)
        return false;
      return true;
    }
    return false;
  }

  filterItemStepsForDropdown(phase: string) {
    let steps = this.distinctPhaseStep.filter(x => x.Phase === phase).map(y => { return { value: y.Step, text: y.Step } })
    return steps;
  }

  isEditable(params: EditableCallbackParams): boolean {
    return params.node.rowPinned ? false : true;
  }

  filterOutTaxAndContingency(node: IRowNode): boolean {
    if (this.applyTaxAndContingencyFilter) {
      return !(node.data.CostCodeCostTypeId === eCostCodeCostTypes.Tax || node.data.CostCodeCostTypeId === eCostCodeCostTypes.Contingency);
    }
    return false;
  }

  private flattenRowData(): void {
    let childrenRows = [];
    this.rowData.forEach(x => {
      if (x.DependentScopingItems != null) {
        childrenRows = childrenRows.concat(x.DependentScopingItems);
      }
    })
    this.rowData = this.rowData.concat(childrenRows);
  }

  private nestRowData(): void {
    let dependentScopingItemsByParentId = new Map<number, ProjectEstimateDetailItemDto[]>();
    let scoped = this.rowData.filter(x => x.ParentItemId !== null);
    scoped.forEach(x => {
      const collection = dependentScopingItemsByParentId.get(x.ParentItemId);
      if (!collection) {
        dependentScopingItemsByParentId.set(x.ParentItemId, [x])
      } else {
        collection.push(x)
      }
    })
    this.rowData.forEach(x => {
      let children = dependentScopingItemsByParentId.get(x.ItemId);
      x.DependentScopingItems = children != null ? children : null;
    });
    this.rowData = this.rowData.filter(x => x.ParentItemId === null || x.ParentItemId === undefined)
  }

  onExportExcel(): void {
    //Bring back all the hidden and grouped data and make it flat
    this.applyTaxAndContingencyFilter = false;
    this.gridApi.onFilterChanged();

    this.flattenRowData();
    this.loadGrid();
    this.setGridGroupState();
    // this.gridApi.onFilterChanged();

    //Export to Excel
    let exportParams: ExcelExportParams = {
      fileName: `${ProjectEstimateStatus[this.estimateStatusId()]}-Estimate-${this.projectEstimate.ProjectId}-${moment().format('MM-DD-YYYY')}.xlsx`,
      skipRowGroups: true
    }

    this.gridApi.exportDataAsExcel(exportParams);

    //Revert all the data back to the original setup
    this.applyTaxAndContingencyFilter = true;
    this.gridApi.onFilterChanged();

    this.nestRowData();
    this.loadGrid();
    this.cdr.detectChanges();
    this.setGridGroupState();
  }

  currencyFormatter(currency: number): string {
    if (currency === null || currency === undefined) {
      return '';
    }
    var formatted = currency.toLocaleString("en-US", { maximumFractionDigits: 6, minimumFractionDigits: 2 });
    return '$' + `${formatted}`;
  }

  customToolTip(params: ITooltipParams): string {
    let toolTipString = this.getToolTipString(params.data);
    return toolTipString;
  }

  private getToolTipString(rowData: any) {
    let newItems = this.newItems;
    let deletedItems = this.deletedItems;
    let updatedItems = this.updatedItems;
    let inactiveItems = this.inactiveItems;

    let toolTip = ``;

    switch (rowData.SyncStatus) {
      case SyncStatuses.StandardConfigNoLongerHasItem:
        let deletedItem = deletedItems.find(x => x.ItemId == rowData.ItemId);
        if (deletedItem) {
          toolTip = `Item # ${deletedItem.ItemNumber}\nDescription: ${deletedItem.DiffDescription}\nAction: ${deletedItem.Action}`;
        }
        break;
      case SyncStatuses.StandardConfigHasNewItem:
        toolTip = `The item(s) will be added to the estimate after sync\n`;
        newItems.forEach((newItem) => {
          toolTip += `\nType: ${ProjectResourceItemType[newItem.ProjectResourceItemTypeId]} | Phase: ${newItem.Phase} | Step: ${newItem.Step} | Item # ${newItem.ItemNumber} | Description: ${newItem.ItemDescription} | Quantity: ${newItem.Quantity} | Unit Rate: ${newItem.Rate}`;
        });
        break;
      case SyncStatuses.MatchesNewScopingScreenItem:
      case SyncStatuses.MatchesNewStandardConfigItem:
        let newItemMatch = newItems.find(x => x.ItemId == rowData.ItemId);
        if (newItemMatch) {
          toolTip = `Item # ${newItemMatch.ItemNumber}\nDescription: ${newItemMatch.DiffDescription}\nAction: ${newItemMatch.Action}`;
        }
        break;
      case SyncStatuses.SourceDiffers:
      case SyncStatuses.QuantityDiffers:
      case SyncStatuses.RateDiffers:
      case SyncStatuses.SourceAndQuantityDiffers:
      case SyncStatuses.SourceAndRateDiffers:
      case SyncStatuses.QuantityAndRateDiffers:
      case SyncStatuses.SourceQuantityAndRateDiffers:
        let updatedItemMatch = updatedItems.find(x => x.ItemId == rowData.ItemId);
        if (updatedItemMatch) {
          toolTip = `Item # ${updatedItemMatch.ItemNumber}`;
          if (updatedItemMatch.ItemDiffs.length > 0) {
            updatedItemMatch.ItemDiffs.forEach(x => {
              toolTip += `\nDiffType: ${SyncStatuses[x.DiffType]} | OldValue: ${x.OldValue} | NewValue: ${x.NewValue} | Action: ${x.Action}`;
            });
          }
        }
        break;
      case SyncStatuses.ItemInactive:
        let inactiveItem = inactiveItems.find(x => x.ItemId == rowData.ItemId);
        if (inactiveItem) {
          toolTip = `Item # ${inactiveItem.ItemNumber}\nDescription: ${inactiveItem.DiffDescription}\nAction: ${inactiveItem.Action}`;
        }
        break;
      default:
        toolTip = `No differences`;
        break;
    }

    return toolTip;
  }

  onMasterGridRowSelected(params: any) {
    // If "Select All" header checkbox of the master grid is selected
    if (params.node.parent && params.node.parent.selected) {
      if (params.node.master && params.node.selected && !params.node.expanded) {
        params.node.setExpanded(true);
        // Scroll to the master row node. This is mandatory to select the items in the detail grid that is not visible in the grid viewport.
        this.gridApi.ensureNodeVisible(params.node, null);
        // Scroll back to the top of the grid.
        this.gridApi.ensureIndexVisible(0, 'top');
      }
      else if (params.node.master && params.node.selected && params.node.expanded && params.node.detailNode && params.node.detailNode.detailGridInfo) {
        params.node.detailNode.detailGridInfo.api.selectAll();
      }
    }
    else if (params.node.parent && !params.node.parent.selected) {
      if (params.node.master && !params.node.selected && params.node.detailNode && params.node.detailNode.detailGridInfo) {
        params.node.detailNode.detailGridInfo.api.deselectAll();
      }
    }
  }

  handleSyncDropDownSelection(key: string) {
    this.dropdownKey = key;
    this.syncTopPinnedRowsByDefault = false;
    let parentRows: ProjectEstimateDetailItemDto[] = [];
    let detailRowsOfParentRows: ProjectEstimateDetailItemDto[] = [];
    this.manualItemsInDraftEstimate = [];

    // Get selected items in the estimate (master) grid
    parentRows = this.gridApi.getSelectedRows() as ProjectEstimateDetailItemDto[];

    this.gridApi.forEachDetailGridInfo((gridInfo) => {
      let detailRows = gridInfo.api.getSelectedRows() as ProjectEstimateDetailItemDto[];
      if (detailRows.length > 0) {
        detailRowsOfParentRows = detailRowsOfParentRows.concat(detailRows);
      }
    });

    // Get all items in master grid
    let allItemsInMasterGrid: ProjectEstimateDetailItemDto[] = [];
    let rowCount = this.gridApi.getDisplayedRowCount();
    for (let i = 0; i < rowCount; i++) {
      let rowNode = this.gridApi.getDisplayedRowAtIndex(i);
      if (rowNode && rowNode.data && !allItemsInMasterGrid.includes(rowNode.data)) {
        allItemsInMasterGrid.push(rowNode.data);
      }
      if (rowNode && rowNode.data && rowNode.data.IsManualAdd) {
        this.manualItemsInDraftEstimate.push(rowNode.data);
      }
    }

    // No checkbox is selected in both master and detail grid
    if (parentRows.length == 0 && detailRowsOfParentRows.length == 0) {
      this.syncTopPinnedRowsByDefault = true;
      parentRows = allItemsInMasterGrid;
      detailRowsOfParentRows = parentRows.map(x => x.DependentScopingItems).reduce((a, b) => a.concat(b), []).filter(x => x != null);
    }
    else if (parentRows.length == allItemsInMasterGrid.length) { // All checkbox in master grid is selected
      this.syncTopPinnedRowsByDefault = true;
    }

    // Combine master and detail rows
    this.selectedDraftItems = parentRows.concat(detailRowsOfParentRows);
    this.populateFinalItemsToSync();
    this.displaySyncDialog();
  }

  private populateFinalItemsToSync() {
    this.itemsToAddAfterSync = [];
    this.itemsToDeleteAfterSync = [];
    this.itemsToUpdateAfterSync = [];
    this.manualItemsToDeleteAfterSync = [];

    let sourceRateQtyDiffStatuses = [
      SyncStatuses.SourceDiffers, SyncStatuses.QuantityDiffers, SyncStatuses.RateDiffers,
      SyncStatuses.SourceAndQuantityDiffers, SyncStatuses.SourceAndRateDiffers, SyncStatuses.QuantityAndRateDiffers, SyncStatuses.SourceQuantityAndRateDiffers
    ];

    if (this.syncTopPinnedRowsByDefault) {
      this.itemsToAddAfterSync = this.newItems;
      // Remove these manual items by default (even if they are not selected) as they will be replaced as new items.
      this.manualItemsToDeleteAfterSync = this.manualItemsInDraftEstimate.filter(x => x.SyncStatus == SyncStatuses.MatchesNewStandardConfigItem);
    }
    else {
      this.manualItemsToDeleteAfterSync = this.selectedDraftItems.filter(x => x.SyncStatus == SyncStatuses.MatchesNewStandardConfigItem);
      if (this.manualItemsToDeleteAfterSync.length > 0) {
        this.manualItemsToDeleteAfterSync.forEach(manualItem => {
          let newItem = this.newItems.find(x => x.Phase == manualItem.Phase && x.Step == manualItem.Step && x.ItemId == manualItem.ItemId);
          this.itemsToAddAfterSync.push(newItem);
        });
      }
    }

    this.itemsToDeleteAfterSync = this.selectedDraftItems.filter(x => x.SyncStatus == SyncStatuses.StandardConfigNoLongerHasItem);
    this.itemsToUpdateAfterSync = this.selectedDraftItems.filter(x => sourceRateQtyDiffStatuses.includes(x.SyncStatus));

    if (this.dropdownKey == 'QuantityAndRate' || this.dropdownKey == 'QuantityOnly') {
      // Remove the selected manual items in the draft estimate
      let manualItemsSelected = this.selectedDraftItems.filter(x => x.ItemId && x.IsManualAdd && x.SyncStatus == SyncStatuses.ItemMatches);
      this.manualItemsToDeleteAfterSync = this.manualItemsToDeleteAfterSync.concat(manualItemsSelected);
    }
  }

  private displaySyncDialog() {
    let syncItemsCount = this.itemsToAddAfterSync.length + this.itemsToDeleteAfterSync.length + this.itemsToUpdateAfterSync.length + this.manualItemsToDeleteAfterSync.length;
    if (syncItemsCount > 0) {
      this.showConfirmSyncDialog = true;
    } else {
      this.nothingToSyncDialog = true;
    }
  }

  public continueToSync() {
    this.showConfirmSyncDialog = false;
    this.syncEstimateItems(this.itemsToAddAfterSync, this.itemsToDeleteAfterSync, this.itemsToUpdateAfterSync, this.manualItemsToDeleteAfterSync);
  }

  private roundToTwoDecimals(value: number): number {
    return round(value + Number.EPSILON, 2);
  }

  public onTaxChanged(value: number): void {
    this.onPercentageChanged('TaxControl', value);
  }

  public onContingencyChanged(value: number): void {
    this.onPercentageChanged('ContingencyControl', value);
  }

  private onPercentageChanged(
    field: 'TaxControl' | 'ContingencyControl',
    value: number
  ): void {
    this.projectEstimate[field] = value;

    if (this.gridApi) {
      this.gridApi.refreshCells({
        force: true,
        columns: ['Tax', 'Contingency', 'TotalCost']
      });
    }
  }

}